import type { FC } from 'react';
import { Suspense, lazy } from 'react';

import { ThemeProvider, tokens } from '@platform/theme-provider';
import GlobalStyle from '@radius/ui-library/dist/GlobalStyle';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import styled, { StyleSheetManager } from 'styled-components';

import { debugError } from './Auxillary/debugLog';
import GlobalErrorFallback from './Components/GlobalErrorFallback';
import Header from './Components/Header';
import LanguageSelector from './Components/LanguageSelector';
import Loading from './Components/Loading';
import './index.css';
import { shouldForwardProp } from './Utils';

ThemeProvider();

const Background = styled.div`
	background-color: ${tokens.backgroundLevel2};
	min-height: 100vh;
	display: flex;
	flex-direction: column;
`;

const LanguageSelectorBar = styled.div`
	padding: 1rem 0 0 1rem;

	> * {
		min-width: 10rem;
		max-width: 12rem;
	}

	&:empty {
		display: none;
	}
`;

const Location = lazy(() => import('./Routes/Location/Location'));
const Video = lazy(() => import('./Routes/Video/Video'));
const GoogleElsVideo = lazy(() => import('./Routes/Video/VideoGoogleEls'));
const ResponderVideo = lazy(() => import('./Routes/ResponderVideo/Video'));
const Directions = lazy(() => import('./Routes/Directions/Directions'));

const App: FC = () => (
	<StyleSheetManager shouldForwardProp={shouldForwardProp}>
		<rd-theme-provider id="rd-theme-provider" data-theme="light">
			<Background>
				<GlobalStyle />
				<ErrorBoundary
					FallbackComponent={GlobalErrorFallback}
					onError={(error, info) => {
						debugError(error, info);
					}}
				>
					<Suspense fallback={<Loading />}>
						<BrowserRouter>
							<Header />
							<LanguageSelectorBar>
								<LanguageSelector />
							</LanguageSelectorBar>
							<Switch>
								{/* local tunnel */}
								<Route path="/external/respondervideo">
									<ResponderVideo />
								</Route>
								<Route path="/external/location">
									<Location />
								</Route>
								<Route path="/external/video">
									<Video />
								</Route>
								<Route path="/external/directions">
									<Directions />
								</Route>

								{/* deployed */}
								<Route path="/respondervideo">
									<ResponderVideo />
								</Route>
								<Route path="/location">
									<Location />
								</Route>
								<Route path="/video">
									<Video />
								</Route>
								<Route path="/googleelsvideo">
									<GoogleElsVideo />
								</Route>
								<Route path="/directions">
									<Directions />
								</Route>
							</Switch>
						</BrowserRouter>
					</Suspense>
				</ErrorBoundary>
			</Background>
		</rd-theme-provider>
	</StyleSheetManager>
);

export default App;
